// AudioGuideContext — owns a SINGLE global <audio> element used to play
// per-property "audio guide" narrations. The element lives in a ref so
// playback survives React route changes (no remount → no interruption).
//
// Behaviour:
// - `playGuide({ src, propertyName, guideTitle, slug })` starts the new
//   guide. If a different guide was already playing, it is stopped and
//   replaced with the new one (only ONE audio guide can play at once).
// - `togglePlay()`, `seek(seconds)`, `setVolume(0..1)`, `stop()` expose
//   the standard transport controls used by FloatingAudioPlayer.
// - Component consumers re-render via state updates derived from the
//   audio element's native events (timeupdate, play, pause, etc.).

import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";

const AudioGuideContext = createContext(null);

const clamp01 = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return 1;
  return Math.min(1, Math.max(0, n));
};

export const AudioGuideProvider = ({ children }) => {
  const audioRef = useRef(null);

  const [meta, setMeta] = useState(null); // { src, propertyName, guideTitle, slug }
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const a = new Audio();
    a.preload = "metadata";
    a.volume = 1;
    audioRef.current = a;

    const onTime = () => setCurrentTime(a.currentTime || 0);
    const onDur  = () => setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    const onPlay = () => { setIsPlaying(true); setLoading(false); };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); };
    const onWait = () => setLoading(true);
    const onCanPlay = () => setLoading(false);

    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("durationchange", onDur);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("waiting", onWait);
    a.addEventListener("canplay", onCanPlay);

    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("durationchange", onDur);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("waiting", onWait);
      a.removeEventListener("canplay", onCanPlay);
      audioRef.current = null;
    };
  }, []);

  const playGuide = useCallback((next) => {
    const a = audioRef.current;
    if (!a || !next?.src) return;
    // Switching guide → reset state.
    if (a.src !== next.src) {
      a.src = next.src;
      setCurrentTime(0);
      setDuration(0);
      setLoading(true);
    }
    setMeta(next);
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => { /* autoplay blocked → user can still hit play */ });
    }
  }, []);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      a.pause();
    }
  }, []);

  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (!a) return;
    const target = Math.max(0, Math.min(t, a.duration || t));
    a.currentTime = target;
    setCurrentTime(target);
  }, []);

  const setVolume = useCallback((v) => {
    const a = audioRef.current;
    const n = clamp01(v);
    if (a) a.volume = n;
    setVolumeState(n);
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setMeta(null);
  }, []);

  const value = {
    meta,
    isPlaying,
    currentTime,
    duration,
    volume,
    loading,
    playGuide,
    togglePlay,
    seek,
    setVolume,
    stop,
  };

  return (
    <AudioGuideContext.Provider value={value}>
      {children}
    </AudioGuideContext.Provider>
  );
};

export const useAudioGuide = () => {
  const ctx = useContext(AudioGuideContext);
  if (!ctx) {
    return {
      meta: null, isPlaying: false, currentTime: 0, duration: 0, volume: 1, loading: false,
      playGuide: () => {}, togglePlay: () => {}, seek: () => {}, setVolume: () => {}, stop: () => {},
    };
  }
  return ctx;
};
